import { BottomOfPage } from "@/components/atoms/BottomOfPage";
import { SelectRounded } from "@/components/atoms/SelectRounded";
import { LoadingSpinner } from "@/components/molecules/LoadingSpinner";
import { Timelapse } from "@/components/timelapse/Timelapse";
import { useScannerGetAll } from "@/lib/api/scanner";
import { useTimelapseGetAll } from "@/lib/api/timelapse";
import { getTimelapseStatusText, TimelapseStatus } from "@/lib/types/timelapse";
import { capitalize } from "@/lib/utils";
import { ComboboxItem } from "@mantine/core";
import { useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

export const TimelapsesOverview = () => {
  const [status, setStatus] = useState<TimelapseStatus | undefined>()
  const [scanner, SetScanner] = useState<number | undefined>()
  const [deleted, setDeleted] = useState(false)

  const { data: scanners, isLoading: isLoadingScanners } = useScannerGetAll()
  const { data: timelapses, isLoading: isLoadingTimelapses, isFetchingNextPage, hasNextPage, fetchNextPage } = useTimelapseGetAll({ status, scanner, deleted })

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: fetchNextPage,
    rootMargin: "0px",
  });

  if (isLoadingScanners || isLoadingTimelapses) return <LoadingSpinner />

  const handleSelectStatus = (option: ComboboxItem | null) => {
    if (!option) {
      setStatus(undefined)
      return
    }

    const status = Object.values(TimelapseStatus).find(o => o === option.value) as TimelapseStatus

    setStatus(status)
  }

  const handleSelectScanner = (option: ComboboxItem | null) => {
    if (!option) {
      SetScanner(undefined)
      return
    }

    const id = Number(option.value)
    const scanner = scanners?.find(s => s.id === id)

    SetScanner(scanner?.id)
  }

  const handleSelectDeleted = (value: string | null) => {
    if (!value) {
      setDeleted(false)
      return
    }

    setDeleted(value.toLowerCase() === "true")
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end gap-2">
        <SelectRounded
          label="Status"
          placeholder="Any"
          clearable
          data={Object.values(TimelapseStatus).map(o => ({ value: o.toString(), label: getTimelapseStatusText(o) }))}
          value={Object.values(TimelapseStatus).find(o => o === status) ?? null}
          onChange={(_, option) => handleSelectStatus(option)}
        />
        <SelectRounded
          label="Scanner"
          placeholder="Any"
          searchable
          clearable
          data={scanners?.map(s => ({ value: s.id.toString(), label: s.name }))}
          value={scanners?.find(s => s.id === scanner)?.id.toString() ?? null}
          onChange={(_, option) => handleSelectScanner(option)}
        />
        <SelectRounded
          label="Deleted"
          allowDeselect={false}
          data={["False", "True"]}
          value={capitalize(String(deleted))}
          onChange={(value) => handleSelectDeleted(value)}
        />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {timelapses?.map(t => <Timelapse key={t.id} timelapse={t} disabled={deleted} />)}
      </div>
      <BottomOfPage ref={sentryRef} showLoading={isFetchingNextPage} hasNextPage={hasNextPage} />
    </div>
  )
}
